import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
// core
import { InputLabel } from '@material-ui/core';
// Components
import Button from '../../components/Button';
import TextField from '../../components/TextField';
import Snackbar from '../../components/Snackbar';
import OutlinedSelect from '../../components/OutlinedSelect';

import useStyles from './styles';
import authService from '../../services/authService';
import validateEmail from '../../utils/validator';
import ImageUpload from '../../components/ImageUpload';

import {
  CLIENT,
  CLIENT_EMPLOYEE,
  SCOUT,
  TELEAUDIT,
} from '../../utils/const/user';
import countries from './countries';

function Register({ update }) {
  const styles = useStyles();
  const { t } = useTranslation();

  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('');
  const [inputValues, setInputValues] = useState({
    fullName: '',
    dni: '',
    countryCode: '',
    email: '',
    phoneNumber: '',
    customPassword: '',
    role: '',
  });

  const handleChange = (e) => {
    const { id, name, value } = e.target;

    if (!id) {
      setInputValues({
        ...inputValues,
        [name]: value,
      });
    } else {
      setInputValues({
        ...inputValues,
        [id]: value,
      });
    }
  };

  const resetForm = () => {
    setInputValues({
      fullName: '',
      dni: '',
      countryCode: 'CL',
      email: '',
      phoneNumber: '',
      customPassword: '',
      role: CLIENT,
      avatar: '',
      qrAuthorization: '',
      bank: '',
      numberAccount: '',
      lastName: '',
    });

    update();
  };

  const handleSend = async () => {
    if (!inputValues.fullName) {
      setSeverity('error');
      setMessage(t('register.messages.invalidName'));
      return;
    }

    if (!inputValues.dni || inputValues.dni.length < 9) {
      setSeverity('error');
      setMessage(t('register.messages.invalidDNI'));
      return;
    }

    if (!inputValues.countryCode) {
      setSeverity('error');
      setMessage(t('register.messages.invalidCountry'));
      return;
    }

    if (!inputValues.email || !validateEmail(inputValues.email)) {
      setSeverity('error');
      setMessage(t('register.messages.invalidEmail'));
      return;
    }

    if (!inputValues.role) {
      setSeverity('error');
      setMessage(t('register.messages.invalidRole'));
      return;
    }

    if (inputValues.dni.length < 8 || inputValues.dni.length > 13) {
      setSeverity('error');
      setMessage(t('register.messages.invalidDNILength'));
      return;
    }

    if (inputValues.fullName.length < 3 || inputValues.fullName.length > 255) {
      setSeverity('error');
      setMessage(t('register.messages.invalidNameLength'));
      return;
    }

    const registerResponse = await authService.register(
      inputValues.fullName,
      inputValues.dni,
      inputValues.countryCode,
      inputValues.email,
      inputValues.customPassword,
      inputValues.role,
      inputValues.phoneNumber,
      inputValues.numberAccount,
      inputValues.qrAuthorization,
      inputValues.avatar,
      inputValues.lastName,
      inputValues.bank,
    );

    if (!registerResponse.success) {
      setSeverity('error');
      setMessage(registerResponse.errorMessage);
      return;
    }

    resetForm();
    setSeverity('success');
    setMessage(t('register.messages.success'));
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSend();
    }
  };

  return (
    <div>
      <div className={styles.register} style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '10px' }}>
        <div>
          <InputLabel>Avatar</InputLabel>
          <ImageUpload
            id="avatar"
            value={inputValues.avatar || ''}
            onChange={(url) => setInputValues({ ...inputValues, avatar: url })}
            label="avatar"
          />
        </div>
        <div>
          <InputLabel>QR Autorización</InputLabel>
          <ImageUpload
            id="qrAuthorization"
            value={inputValues.qrAuthorization || ''}
            onChange={(url) => setInputValues({ ...inputValues, qrAuthorization: url })}
            label="QR"
          />
        </div>
        <div>
          <InputLabel>{t('register.labels.name')}</InputLabel>
          <TextField
            variant="outlined"
            id="fullName"
            value={inputValues.fullName}
            onChange={handleChange}
            fullWidth
            InputProps={{
              onKeyDown: handleKeyDown,
            }}
          />
        </div>
        <div>
          <InputLabel>Apellido</InputLabel>
          <TextField
            id='lastName'
            name='lastName'
            value={inputValues.lastName || ''}
            variant="outlined"
            onChange={handleChange}
            fullWidth
          />
        </div>
        <div>
          <InputLabel>{t('register.labels.dni')}</InputLabel>
          <TextField
            variant="outlined"
            id="dni"
            value={inputValues.dni}
            onChange={handleChange}
            fullWidth
            InputProps={{
              onKeyDown: handleKeyDown,
            }}
          />
        </div>
        <div>
          <InputLabel>{t('register.labels.country')}</InputLabel>
          <OutlinedSelect
            name="countryCode"
            items={countries}
            value={inputValues.countryCode}
            onChange={handleChange}
            fullWidth
            InputProps={{
              onKeyDown: handleKeyDown,
            }}
          />
        </div>
        <div>
          <InputLabel>{t('register.labels.email')}</InputLabel>
          <TextField
            variant="outlined"
            id="email"
            value={inputValues.email}
            onChange={handleChange}
            fullWidth
            InputProps={{
              onKeyDown: handleKeyDown,
            }}
          />
        </div>
        <div>
          <InputLabel>{t('register.labels.password')}</InputLabel>
          <TextField
            variant="outlined"
            id="customPassword"
            type="password"
            disabled
            value={inputValues.customPassword}
            onChange={handleChange}
            fullWidth
            InputProps={{
              onKeyDown: handleKeyDown,
            }}
          />
        </div>
        <div>
          <InputLabel>Banco</InputLabel>
          <TextField
            name='bank'
            id="bank"
            value={inputValues.bank || ''}
            variant="outlined"
            onChange={handleChange}
            fullWidth
          />
        </div>
        <div>
          <InputLabel>Número de Cuenta</InputLabel>
          <TextField
            name='numberAccount'
            id="numberAccount"
            value={inputValues.numberAccount || ''}
            variant="outlined"
            onChange={handleChange}
            fullWidth
            type="number"
          />
        </div>
        <div>
          <InputLabel>{t('register.labels.phone')}</InputLabel>
          <TextField
            variant="outlined"
            id="phoneNumber"
            value={inputValues.phoneNumber}
            onChange={handleChange}
            fullWidth
            InputProps={{
              onKeyDown: handleKeyDown,
            }}
          />
        </div>
        <div className={styles.selectRole}>
          <InputLabel>{t('register.labels.role')}</InputLabel>
          <OutlinedSelect
            name="role"
            items={[
              { value: CLIENT, name: CLIENT },
              { value: CLIENT_EMPLOYEE, name: CLIENT_EMPLOYEE },
              { value: SCOUT, name: SCOUT },
              { value: TELEAUDIT, name: TELEAUDIT },
            ]}
            value={inputValues.role}
            onChange={handleChange}
            fullWidth
            InputProps={{
              onKeyDown: handleKeyDown,
            }}
          />
        </div>
        <div />
        <Button
          id="submit-btn"
          onClick={handleSend}
          text={t('register.button')}
          height="35px"
          width="100%"
          backgroundColor="theme-secondary-blue"
          color="theme-primary-white"
          borderRadius="10px"
        />
      </div>
      <div>
        <Snackbar
          open={message !== ''}
          message={message}
          severity={severity}
          onClose={() => setMessage('')}
        />
      </div>
    </div>
  );
}

export default Register;
